import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Heart } from "lucide-react";
import Seo from "@/components/Seo";
import NewsletterForm from "@/components/NewsletterForm";
import { MaskedLines, FadeUp, Overline, GoldRule } from "@/components/Reveal";
import { TerraButton, GhostButton } from "@/components/Buttons";
import { IMG, SITE, ASSETS } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OFFERINGS = [
  {
    title: "Supper Club",
    copy: "An intimate, ticketed dining experience where strangers become friends over a curated multi-course meal. Held monthly in Jersey City.",
    img: IMG.cardSupperClub,
    to: "/supper-club",
    testid: "home-offering-supper-club",
  },
  {
    title: "Catering",
    copy: "Private dinners, celebrations, and corporate gatherings for up to 30 people. Bold menus, full service, nothing generic.",
    img: IMG.cardCatering,
    to: "/catering",
    testid: "home-offering-catering",
  },
  {
    title: "Meal Prep",
    copy: "A fresh weekly menu, cooked from scratch and ready for your fridge. Order right here.",
    img: IMG.privateMealPrep,
    to: "/meal-prep",
    testid: "home-offering-meal-prep",
  },
  {
    title: "Private Events & Chef Services",
    copy: "Borrow our space, hire us for in-home meal prep, or bring on a personal chef.",
    img: IMG.cardPrivate,
    to: "/private-events",
    testid: "home-offering-private-events",
  },
];

const PHRASES = [
  { a: "Where sourcing", b: " begins with care.", pos: "left-[7%] top-[20%] text-left" },
  { a: "Where healing", b: " becomes flavor.", pos: "right-[6%] top-[16%] text-right" },
  { a: "Where nourishment", b: " is felt deeply.", pos: "bottom-[24%] left-[9%] text-left" },
  { a: "Where meals turn", b: " into connection.", pos: "bottom-[18%] right-[6%] text-right" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.45, 0.7]);
  const [guestWords, setGuestWords] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(({ data }) => setGuestWords(data)).catch(() => setGuestWords([]));
    axios.get(`${API}/reviews/public`).then(({ data }) => setReviews(data)).catch(() => setReviews([]));
  }, []);

  return (
    <div>
      <Seo
        title="Sita — Supper Club, Catering & Private Chef · Jersey City, NJ"
        description="Bold food. Intentional tables. A seat for everyone. Sita is a supper club, catering, and private chef company in Jersey City, NJ. Earth found, home cooked."
      />

      {/* ---------- Hero ---------- */}
      <section ref={heroRef} data-testid="home-hero" className="relative flex min-h-[92vh] items-center justify-center overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0" initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}>
          <img
            src={IMG.homeHero}
            alt="An aerial view of green farm rows — from the soil"
            className="h-[115%] w-full object-cover"
          />
        </motion.div>
        <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-deepforest" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-deepforest/40 via-transparent to-cream" aria-hidden="true" />

        {/* reel-style floating phrases */}
        <div className="pointer-events-none absolute inset-0 hidden lg:block" aria-hidden="true">
          {PHRASES.map((p, i) => (
            <motion.span
              key={p.a}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4 + i * 0.2, duration: 1 }}
              className={`absolute font-display text-lg italic leading-snug text-cream/85 ${p.pos}`}
            >
              {p.a} <span className="text-gold">{p.b}</span>
            </motion.span>
          ))}
        </div>

        <div className="relative z-10 mx-auto max-w-5xl px-6 pb-24 pt-28 text-center text-cream">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="text-xs font-medium uppercase tracking-[0.28em] text-gold"
          >
            Supper club · Catering · Meal prep · Private chef — Jersey City, NJ
          </motion.p>
          <MaskedLines
            delay={0.3}
            className="mt-6 font-display text-5xl font-light leading-[1.05] sm:text-6xl lg:text-7xl"
            lines={[
              "Bold food. Intentional tables.",
              <span key="l2" className="italic text-gold">A seat for everyone.</span>,
            ]}
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mx-auto mt-8 max-w-2xl text-base leading-relaxed text-cream/90 md:text-lg"
          >
            Sita is a supper club and catering company rooted in the belief that good food is a right, not a luxury.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <TerraButton href={SITE.stanStoreUrl} external testid="hero-reserve-btn">
              Join the next supper club <ArrowRight size={16} />
            </TerraButton>
            <GhostButton to="/inquiries" dark testid="hero-inquire-btn">
              Work with us
            </GhostButton>
          </motion.div>
        </div>
      </section>

      {/* ---------- Short about ---------- */}
      <section data-testid="home-about" className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-32">
        <div className="grid items-center gap-12 md:grid-cols-12">
          <FadeUp className="md:col-span-5">
            <div className="overflow-hidden rounded-b-3xl rounded-t-[10rem]">
              <img
                src={IMG.homeAbout}
                alt="Pouring wine at a shared meal"
                className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
              />
            </div>
          </FadeUp>
          <div className="md:col-span-6 md:col-start-7">
            <FadeUp>
              <Overline>Where Sita comes from</Overline>
              <GoldRule className="mt-5" />
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-8 font-display text-2xl leading-snug text-forest md:text-[1.7rem]">
                Sita was born from a deep love of bold, culturally rich flavors — food made with intention.
              </p>
              <p className="mt-6 leading-relaxed text-ink/80">
                Every dish is made with intention. Every table is set with care. Whether you are joining us for a supper
                club or hiring us to cater your next event, you will leave having experienced something real.
              </p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <Link
                to="/about"
                data-testid="home-about-read-story-link"
                className="link-underline mt-8 inline-flex items-center gap-2 text-sm font-medium text-terracotta"
              >
                Read our full story <ArrowRight size={15} />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ---------- Our essence ---------- */}
      <section data-testid="home-essence" className="relative overflow-hidden bg-forest text-cream">
        <img
          src={ASSETS.sitaLogo}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -right-20 top-1/2 hidden h-[30rem] w-[30rem] -translate-y-1/2 rotate-12 opacity-10 lg:block"
        />
        <div className="relative mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <FadeUp>
                <Overline light>Our essence</Overline>
                <h2 className="mt-4 font-display text-4xl font-light md:text-5xl">
                  Our <span className="italic text-gold">essence.</span>
                </h2>
              </FadeUp>
              <div className="mt-10 space-y-5">
                {[
                  ["Where sourcing", " begins with care."],
                  ["Where healing", " becomes flavor."],
                  ["Where nourishment", " is felt deeply."],
                  ["Where meals turn", " into connection."],
                ].map(([a, b], i) => (
                  <FadeUp key={a} delay={i * 0.1}>
                    <p className="font-display text-2xl font-light leading-snug md:text-3xl">
                      {a} <span className="italic text-gold">{b}</span>
                    </p>
                  </FadeUp>
                ))}
              </div>
            </div>
            <div className="lg:col-span-7 lg:pt-2">
              <FadeUp delay={0.1}>
                <p className="text-lg leading-relaxed text-cream/90">
                  Sita offers small-batch meal prep, soulful catering, and intimate supper clubs. We cook with whole,
                  vibrant ingredients, thoughtfully chosen, never rushed. Each dish is crafted to nourish fully: rich in
                  flavor, dense in nutrients, and filled with intention.
                </p>
                <p className="mt-6 leading-relaxed text-cream/80">
                  Whether it's weekday meals or gatherings under string lights, we believe food should comfort, energize,
                  and invite you in.
                </p>
              </FadeUp>
              <FadeUp delay={0.2}>
                <span className="my-10 block h-px w-14 bg-gold" aria-hidden="true" />
                <h3 className="font-display text-3xl font-light md:text-4xl">
                  Sita is a <span className="italic text-gold">return.</span>
                </h3>
                <p className="mt-6 leading-relaxed text-cream/85">
                  To food that nourishes deeply, to quiet traditions, to meals made with memory in mind. Rooted in care
                  and intuition, Sita was born from the belief that food is more than fuel. It is story, it is healing,
                  it is connection. Seasonal selections of produce shape what we create, and the hands that prepare it
                  do so with intention.
                </p>
                <p className="mt-6 leading-relaxed text-cream/85">
                  Every meal begins in the heart and ends at the table with warmth, generosity, and love.
                </p>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- What we do ---------- */}
      <section data-testid="home-offerings" className="border-t border-forest/10">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
          <FadeUp className="max-w-2xl">
            <Overline>What we do</Overline>
            <h2 className="mt-4 font-display text-4xl font-light text-forest md:text-5xl">Four ways to pull up a chair</h2>
          </FadeUp>
          <div className="mt-12 grid gap-8 md:grid-cols-2 xl:grid-cols-4">
            {OFFERINGS.map((o, i) => (
              <FadeUp key={o.title} delay={i * 0.1} className="h-full">
                <Link
                  to={o.to}
                  data-testid={o.testid}
                  className="group flex h-full flex-col overflow-hidden rounded-3xl border border-forest/10 bg-white/40 transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-forest/10"
                >
                  <div className="overflow-hidden">
                    <img src={o.img} alt={o.title} className="h-48 w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-xl text-forest md:text-2xl">{o.title}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/75">{o.copy}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-terracotta transition-transform duration-300 group-hover:translate-x-1">
                      Explore <ArrowRight size={15} />
                    </span>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Guest words ---------- */}
      <section data-testid="home-social-proof" className="bg-deepforest text-cream">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
          <FadeUp>
            <Overline light>From the table</Overline>
            <h2 className="mt-4 max-w-xl font-display text-4xl font-light md:text-5xl">Guest words</h2>
          </FadeUp>
          {(() => {
            const reviewItems = reviews.slice(0, 3).map((r) => ({
              id: r.id,
              quote: r.suggestions || "",
              name: (r.name || "").trim() ? r.name.trim().split(" ")[0] : "Supper club guest",
              context: r.service_type,
              hearts: r.heart_rating || 0,
              fav: r.favorite_dish || "",
            }));
            const items = [...reviewItems];
            if (items.length < 3) {
              for (const t of guestWords) {
                if (items.length >= 3) break;
                items.push({ id: t.id, quote: t.quote, name: t.guest, context: t.context, hearts: 0, fav: "" });
              }
            }
            return (
              <div className="mt-12 grid gap-6 md:grid-cols-3" data-testid="home-guest-words">
                {[IMG.proofA, IMG.proofB, IMG.proofC].map((src, i) => {
                  const t = items[i];
                  return (
                    <FadeUp key={src} delay={i * 0.12}>
                      <figure data-testid={`guest-word-card-${i + 1}`} className="group relative aspect-[4/5] overflow-hidden rounded-3xl">
                        <img
                          src={src}
                          alt={t ? "Guest words from a Sita table" : "A dish from a Sita table"}
                          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                        />
                        {t && (
                          <>
                            <div className="absolute inset-0 bg-gradient-to-t from-deepforest/95 via-deepforest/40 to-transparent" aria-hidden="true" />
                            <blockquote className="absolute inset-x-0 bottom-0 p-6">
                              {t.hearts > 0 && (
                                <span className="mb-3 flex gap-0.5 text-terracotta" aria-label={`${t.hearts} out of 5 hearts`}>
                                  {[1, 2, 3, 4, 5].map((n) => (
                                    <Heart key={n} size={13} fill={n <= t.hearts ? "currentColor" : "none"} strokeWidth={1.75} />
                                  ))}
                                </span>
                              )}
                              {t.quote && <p className="font-display text-lg italic leading-snug text-cream">&ldquo;{t.quote}&rdquo;</p>}
                              {t.fav && !t.quote && <p className="font-display text-lg italic leading-snug text-cream">Favorite: {t.fav}</p>}
                              <figcaption className="mt-3 text-[11px] uppercase tracking-[0.18em] text-gold">
                                {t.name}{t.context ? ` · ${t.context}` : ""}
                              </figcaption>
                            </blockquote>
                          </>
                        )}
                      </figure>
                    </FadeUp>
                  );
                })}
              </div>
            );
          })()}
          <FadeUp delay={0.2}>
            <Link
              to="/feedback"
              data-testid="leave-review-link"
              className="link-underline mt-10 inline-flex items-center gap-2 text-sm font-medium text-cream/80 hover:text-gold"
            >
              Leave a review <ArrowRight size={15} />
            </Link>
          </FadeUp>
          {guestWords.length === 0 && (
            <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
              {[
                { src: IMG.proofA, alt: "Handmade dumplings plated at a Sita supper club" },
                { src: IMG.proofB, alt: "Mango cheesecake cups, plated by Sita" },
                { src: IMG.proofC, alt: "Savory waffles with chutneys, plated for guests" },
              ].map((photo, i) => (
                <FadeUp key={photo.src} delay={i * 0.1}>
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    data-testid={`home-proof-photo-${i + 1}`}
                    className="aspect-[4/3] w-full rounded-2xl object-cover"
                  />
                </FadeUp>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ---------- Newsletter ---------- */}
      <section data-testid="home-newsletter" className="mx-auto max-w-3xl px-6 py-20 text-center md:py-28">
        <FadeUp>
          <Overline>Email list</Overline>
          <h2 className="mt-4 font-display text-4xl font-light text-forest md:text-5xl">Stay at the table.</h2>
          <p className="mx-auto mt-5 max-w-xl leading-relaxed text-ink/80">
            Join the Sita email list for upcoming supper club dates, menu previews, and first access to tickets.
          </p>
          <div className="mt-8 flex justify-center">
            <NewsletterForm variant="light" testidPrefix="home" />
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
